import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  AlarmClock,
  BarChart3,
  Briefcase,
  CalendarRange,
  CircleDollarSign,
  ClipboardList,
  Gauge,
  Layers,
  LineChart,
  Phone,
  Receipt,
  ScrollText,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { SiteShell } from "@/components/site/SiteShell";
import { BenefitGrid } from "@/components/landing/BenefitGrid";
import { WhyTtite } from "@/components/landing/WhyTtite";
import { FeeFactors } from "@/components/finance/FeeFactors";
import { FinanceHero } from "@/components/finance/FinanceHero";
import { FinanceTrust } from "@/components/finance/FinanceTrust";
import { HowItWorks } from "@/components/finance/HowItWorks";
import { PaymentManagement } from "@/components/finance/PaymentManagement";
import { ScopeGroups } from "@/components/finance/ScopeGroups";
import { ScopingForm } from "@/components/finance/ScopingForm";
import { SupportLevels } from "@/components/finance/SupportLevels";
import { TeamStructure } from "@/components/finance/TeamStructure";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/landingLinks";

const SLUG = "outsourced-financial-management";
const ACCENT = "#5b4a8a";

const OUTGROWN = [
  {
    icon: TrendingUp,
    title: "Your business has grown",
    text: "The finance requirement has become too complex for bookkeeping alone.",
  },
  {
    icon: Users,
    title: "You don't have a complete finance team",
    text: "You need financial management capability without building the whole function internally.",
  },
  {
    icon: BarChart3,
    title: "Management needs better information",
    text: "You need regular reporting, forecasting and financial insight to support decisions.",
  },
];

const ROLES = [
  {
    icon: UserCog,
    title: "Financial Manager",
    text: "Leads the finance function: management reporting, cash-flow management, planning, controls and financial oversight.",
  },
  {
    icon: LineChart,
    title: "Accountant",
    text: "Supports reporting, reconciliations, month-end processes, balance-sheet management and financial analysis.",
  },
  {
    icon: ClipboardList,
    title: "Finance Officer / Bookkeeper",
    text: "Handles day-to-day transaction processing, operational finance and accounting administration.",
  },
];

const SCOPE_GROUPS = [
  {
    icon: Receipt,
    title: "Day-to-day finance",
    caption: "The processing that has to happen every week, whoever is watching.",
    items: [
      "Bookkeeping and transaction processing",
      "Bank reconciliations",
      "Customer and supplier reconciliations",
      "Debtors and creditors administration",
      "Payroll processing",
      "Supplier invoice processing",
      "Tax and statutory submissions",
      "Supplier payment administration, where required",
    ],
  },
  {
    icon: Gauge,
    title: "Financial management",
    anchorId: "financial-management",
    caption: "The layer most growing businesses are missing entirely.",
    items: [
      "Monthly management accounts",
      "Cash-flow management and forecasting",
      "Budgets, with actual results tracked against them",
      "KPI and performance reporting",
      "Working-capital management",
      "Financial process and control oversight",
      "Financial decision support",
    ],
  },
  {
    icon: ScrollText,
    title: "Reporting & stakeholder support",
    anchorId: "reporting-support",
    caption: "What goes out to the people who rely on your numbers.",
    items: [
      "Annual financial statements",
      "Management and board reporting",
      "Funder and lender reporting",
      "Audit coordination",
      "SARS and statutory matters",
      "Information requested by external stakeholders",
    ],
  },
];

const SUPPORT_LEVELS = [
  {
    icon: AlarmClock,
    title: "Flexible support",
    text: "A finance team allocated for agreed hours each week, giving you the capacity you need without paying for unused full-time resources.",
    detail: "Best when the gap is specific",
  },
  {
    icon: CalendarRange,
    title: "Regular part-time team",
    text: "A structured team working with your business on agreed days each week, with the Financial Manager providing ongoing oversight.",
    detail: "Structured ongoing support",
  },
  {
    icon: Briefcase,
    title: "Dedicated finance function",
    text: "A dedicated outsourced team focused on your business full-time, operating as your finance department under a Financial Manager.",
    detail: "Your finance department, outsourced",
  },
];

const FEE_FACTORS = [
  {
    icon: Users,
    title: "Team composition",
    text: "The mix of Financial Manager, Accountant and Finance Officer support required.",
  },
  {
    icon: CalendarRange,
    title: "Time allocation",
    text: "Whether support is needed for selected hours, selected days, or on a dedicated full-time basis.",
  },
  {
    icon: Receipt,
    title: "Transaction & operational volume",
    text: "The volume of bookkeeping, debtors, creditors, payroll and finance administration.",
  },
  {
    icon: BarChart3,
    title: "Reporting requirements",
    text: "Management accounts, cash-flow forecasts, budgets, KPIs and board reporting.",
  },
  {
    icon: Wallet,
    title: "Payment management",
    text: "Whether T-TITE prepares and administers supplier payment runs.",
  },
  {
    icon: Layers,
    title: "Business complexity",
    text: "Entities, employees, locations, reporting structures and anything else that adds moving parts.",
  },
];

/* Section anchors, also used as Google Ads sitelink destinations. */
const ANCHORS = [
  "more-than-bookkeeping",
  "finance-team",
  "financial-management",
  "reporting-support",
  "flexible-support",
  "scope",
];

const WHY_TTITE = [
  {
    icon: UserCog,
    title: "One team. Clear accountability.",
    text: "Your day-to-day accounting and financial management sit within one coordinated function, led by a Financial Manager who oversees the reporting and finance team.",
  },
  {
    icon: LineChart,
    title: "Financial information you can use",
    text: "Timely reporting and practical insight, so management can see performance, cash flow and the areas that need attention.",
  },
  {
    icon: CircleDollarSign,
    title: "Scale the team as you change",
    text: "Increase or reduce the level of support as your requirements evolve, without rebuilding an internal finance department each time.",
  },
];

/**
 * Outsourced financial management.
 *
 * The one service on the site that is deliberately not priced online: the team
 * mix, the days worked and whether we prepare supplier payment runs change the
 * monthly fee far too much for a self-serve figure to be honest. The page
 * earns a scoping conversation instead, and the brief it collects is
 * structured enough to be useful before anyone picks up the phone.
 */
export default function OutsourcedFinancialManagementPage() {
  const formRef = useRef(null);
  const { hash } = useLocation();

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  /**
   * Arriving at /outsourced-financial-management#scope - which is where the
   * calculator sends a larger bookkeeping enquiry - should land on the brief,
   * not the top of the page. The same is true of the section anchors used as
   * Google Ads sitelinks. A router link changes the hash without the browser
   * jumping anywhere, and on a cold visit the section has not painted yet, so
   * we do the scrolling ourselves once it exists. Each anchored section carries
   * its own scroll-mt so the heading clears the sticky header.
   */
  useEffect(() => {
    const id = (hash || "").replace("#", "");
    if (!ANCHORS.includes(id)) return;

    let attempts = 0;
    const land = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      /* Still rendering: look again shortly, but never forever. */
      attempts += 1;
      if (attempts < 20) timer = setTimeout(land, 100);
    };

    let timer = setTimeout(land, 120);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <SiteShell testId="outsourced-financial-management-page">
      <div style={{ "--tt-accent": ACCENT }}>
        <FinanceHero onScope={scrollToForm} />

        <FinanceTrust />

        <BenefitGrid
          slug={SLUG}
          tinted
          anchorId="more-than-bookkeeping"
          heading="When your business needs more than bookkeeping"
          lead="As businesses grow, keeping the accounting up to date is only part of the requirement. Management also needs reliable reporting, cash-flow visibility, stronger controls and someone taking responsibility for the finance function."
          note="Typically suited to businesses with annual revenue above R20 million, or businesses whose finance requirements have outgrown a standard bookkeeping service."
          items={OUTGROWN}
        />

        <TeamStructure roles={ROLES} />

        <ScopeGroups groups={SCOPE_GROUPS} />

        <PaymentManagement onScope={scrollToForm} />

        <SupportLevels levels={SUPPORT_LEVELS} />

        <FeeFactors factors={FEE_FACTORS} />

        <HowItWorks />

        <WhyTtite
          slug={SLUG}
          heading="Why entrust your finance function to us?"
          lead="One coordinated finance function, led by a Financial Manager, with the level of support scaled to what your business needs."
          items={WHY_TTITE}
        />

        {/* closing ask */}
        <section
          data-testid={`${SLUG}-final-cta`}
          className="bg-[color:var(--tt-teal-deep)]"
        >
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
            <h2 className="max-w-[30ch] text-[27px] font-bold leading-[1.15] tracking-[-0.015em] text-white sm:text-[34px]">
              Build the finance function your business needs
            </h2>
            <p className="mt-4 max-w-[58ch] text-[15px] leading-relaxed text-white/80 sm:text-base">
              Tell us about your business and we&apos;ll recommend a finance-team structure built
              around your operational and management requirements.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                size="lg"
                onClick={scrollToForm}
                data-testid={`${SLUG}-final-scope`}
                className="h-12 min-h-[44px] bg-white px-5 text-[15px] font-semibold text-[color:var(--tt-teal-deep)] hover:bg-white/90"
              >
                Scope my finance function
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid={`${SLUG}-final-call`}
                className="h-12 min-h-[44px] border-white/40 bg-transparent px-5 text-[15px] font-semibold text-white hover:bg-white/10 hover:text-white"
              >
                <a href={PHONE_HREF}>
                  <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                  {PHONE_DISPLAY}
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* The brief, and the last thing on the page on purpose: somebody who
            has just filled it in should not meet another ask to do the same. */}
        <section
          ref={formRef}
          id="scope"
          data-testid={`${SLUG}-form-section`}
          className="scroll-mt-24 bg-[color:var(--tt-surface-2)] py-14 sm:py-20"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-4">
                <span className="tt-stamp">Scope my finance function</span>
                <h2 className="mt-5 text-[27px] font-bold leading-[1.15] tracking-[-0.015em] text-foreground sm:text-[34px]">
                  Tell us what your finance function needs to handle
                </h2>
                <p className="mt-4 max-w-[44ch] text-[15px] leading-relaxed text-muted-foreground">
                  A few short steps give us enough context to recommend a tailored team
                  structure rather than a generic quote. You&apos;ll see your whole brief before
                  anything is sent.
                </p>
                <p className="mt-5 text-[13px] leading-relaxed text-muted-foreground">
                  Rather talk it through?{" "}
                  <a href={PHONE_HREF} className="tt-link font-semibold">
                    {PHONE_DISPLAY}
                  </a>
                </p>
              </div>

              <div className="lg:col-span-8">
                <ScopingForm />
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteShell>
  );
}
